import { Center, Flex, Loader, Paper, Table, Tabs, Text, Title, createStyles } from "@mantine/core";
import { formatDate, formatDateTime } from "@medplum/core";
import { DiagnosticReport } from "@medplum/fhirtypes";
import { StatusBadge, useMedplum } from "@medplum/react";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

const useStyles = createStyles((theme) => ({
  table: {
    marginTop: "30px",
  },
  thead: {
    backgroundColor: "#BEDEF8",
    borderRadius: 4,
  },
  text: {
    border: "1px solid #ddd",
    borderRadius: "5px",
    padding: "12px",
    marginTop: "25px",
    overflow: "auto",
    wordWrap: "break-word",
    maxWidth: "100%",
  },
  paper: {
    marginTop: "20px",
    borderRadius: "8px",
    marginLeft: "40px",
    marginRight: "40px",
    padding: "20px",
    background: "'#FFF",
    boxShadow: "0px 1px 24px 2px rgba(0, 0, 0, 0.06)",
  },
}));


export const DiagnosticReportDetails = () => {
  const location = useLocation();
  const medplum = useMedplum();
  const { classes } = useStyles();
  const { resultId = "" } = useParams();
  const query = new URLSearchParams(location.search);
  const patientName = query.get("name") as string;
  const [observations, setObservations] = useState([]);
  const [isFetching, setIsFetching] = useState<boolean>(true);

  const tableHeaders = [
    "Report Name",
    "Result Count",
    "Issued on",
    "Category",
    ,
    "Performer",
    "Status",
  ];

  const resource: DiagnosticReport = medplum
    .readResource("DiagnosticReport", resultId)
    .read();

  useEffect(() => {
    setIsFetching(false);
  }, []);

  const getObservation = async () => {
    let tempArr: any = [];
    if (resource.result) {
      for (let observation of resource.result as any) {
        try {
          let res = await medplum.readResource(
            "Observation",
            observation.reference.split("/")[1]
          );
          tempArr.push(res);
        } catch (err) {
          console.error(err);
        }
      }
    }
    setObservations(tempArr);
    setIsFetching(false);
  };

  const getCategory = (category: any) => {
    let text = "";
    if (category?.length > 0) {
      for (let i = 0; i < category.length; i++) {
        if (category[i].coding) {
          for (let j = 0; j < category[i].coding.length; j++) {
            text = category[i].coding[j].display;
          }
        }
      }
      return text;
    }
  };

  return (
    <>
      <Paper className={classes.paper}>
        <Title order={2}>Diagnostic report</Title>
        <Flex gap={"40px"} mt={"lg"}>
          <div>
            <Title color="rgba(129, 137, 153, 1)" size={"14px"} weight={500}>
              Subject
            </Title>
            <Title size={"18px"} weight={600}>
              {patientName }
            </Title>
          </div>
          <div>
            <Title color="rgba(129, 137, 153, 1)" size={"14px"} weight={500}>
              Patient Name
            </Title>
            <Title size={"18px"} weight={600}>
              {patientName}
            </Title>
          </div>
          <div>
            <Title color="rgba(129, 137, 153, 1)" size={"14px"} weight={500}>
              Status
            </Title>
            <Title
              size={"18px"}
              weight={600}
              sx={{ textTransform: "capitalize" }}
            >
              {resource.status}
            </Title>
          </div>
        </Flex>
        <Tabs
          keepMounted={false}
          defaultValue="first"
          sx={{ marginTop: "20px" }}
        >
          <Tabs.List sx={{ width: "283px" }}>
            <Tabs.Tab value="first"> Diagnostic report</Tabs.Tab>
            <Tabs.Tab value="second">FHIR response</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="first">
            <Table className={classes.table}>
              <thead className={classes.thead}>
                <tr>
                  {tableHeaders.map((val) => {
                    return <th key={val}>{val}</th>;
                  })}
                </tr>
              </thead>
              {
                <tbody>
                  {
                    <tr key={resource.id}>
                      <td>{resource?.code?.text}</td>
                      <td>{resource?.result?.length}</td>
                      <td>{formatDate(resource?.issued?.split("T")[0])}</td>
                      <td>{resource?.category?.[0]?.text}</td>
                      <td>{resource?.performer?.[0]?.display}</td>
                      <td>
                        <StatusBadge status={resource.status as string} />
                      </td>
                    </tr>
                  }
                </tbody>
              }
            </Table>
            {Object.keys(resource)?.length === 0 && !isFetching && (
              <Center style={{ width: "100%", height: "200px" }}>
                <Text size="xl" color="dimmed">
                  No results
                </Text>
              </Center>
            )}
            {isFetching && (
              <Center style={{ width: "100%", height: "200px" }}>
                <Loader />
              </Center>
            )}
          </Tabs.Panel>
          <Tabs.Panel value="second">
            {" "}
            <Text className={classes.text}>
              <pre>{JSON.stringify(resource, null, 2)}</pre>
            </Text>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </>
  );
};